import React, { useState, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Pressable, 
  Animated,
  Dimensions,
  Platform
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// SVG Icons
const SunIcon = ({ color = '#000', size = 20 }) => {
  if (Platform.OS !== 'web') return <Text style={{ color, fontSize: size }}>Light</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
};

const MoonIcon = ({ color = '#000', size = 20 }) => {
  if (Platform.OS !== 'web') return <Text style={{ color, fontSize: size }}>Dark</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
};

// Responsive breakpoints
const getBreakpoint = (width) => {
  if (width < 480) return 'mobile';
  if (width < 768) return 'mobileLarge';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Animated button with hover
const AnimatedButton = ({ children, onPress, style, disabled }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onPressIn={() => Animated.spring(scale, { toValue: 0.96, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.03, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[style, { transform: [{ scale }] }]}>{children}</Animated.View>
    </Pressable>
  );
};

// Nav pill with hover effect
const NavPill = ({ label, onPress, theme, isActive = false }) => {
  const scale = useRef(new Animated.Value(1)).current;
  const bgOpacity = useRef(new Animated.Value(isActive ? 1 : 0)).current;

  return (
    <Pressable
      onHoverIn={() => { 
        Animated.parallel([
          Animated.spring(scale, { toValue: 1.05, useNativeDriver: true }), 
          Animated.timing(bgOpacity, { toValue: 1, duration: 150, useNativeDriver: true })
        ]).start(); 
      }}
      onHoverOut={() => { 
        Animated.parallel([
          Animated.spring(scale, { toValue: 1, useNativeDriver: true }), 
          Animated.timing(bgOpacity, { toValue: isActive ? 1 : 0, duration: 150, useNativeDriver: true })
        ]).start(); 
      }}
      onPress={onPress}
    >
      <Animated.View style={[styles.navPill, { transform: [{ scale }] }]}>
        <Animated.View style={[
          styles.navPillBg, 
          { backgroundColor: isActive ? theme.primaryLight + '20' : theme.surfaceHover, opacity: bgOpacity }
        ]} />
        <Text style={[styles.navPillText, { color: isActive ? theme.primary : theme.textSecondary }]}>{label}</Text>
      </Animated.View>
    </Pressable>
  );
};

/**
 * Unified Navbar component for all pages
 * @param {Object} props
 * @param {Object} props.navigation - React Navigation object
 * @param {Animated.Value} [props.scrollY] - Optional scroll position for animations
 * @param {boolean} props.isDark - Dark mode state
 * @param {Function} props.toggleDarkMode - Toggle dark mode function
 * @param {string} props.currentRoute - Current route name (Landing, Plans, Library, etc.)
 * @param {Function} [props.scrollToSection] - Optional function to scroll to sections (Landing only)
 * @param {number} props.screenWidth - Current screen width
 */
export const Navbar = ({ 
  navigation, 
  scrollY, 
  isDark, 
  toggleDarkMode, 
  currentRoute = 'Landing',
  scrollToSection,
  screenWidth 
}) => {
  const { user } = useAuth();
  const [scrollPercent, setScrollPercent] = useState(0);
  const navbarScale = useRef(new Animated.Value(1)).current;

  const breakpoint = getBreakpoint(screenWidth);
  const isMobileNav = breakpoint === 'mobile' || breakpoint === 'mobileLarge';
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  // Handle scroll-based animations (only if scrollY is provided)
  useEffect(() => {
    if (!scrollY) return;
    
    const listener = scrollY.addListener(({ value }) => {
      const percent = Math.min(value / (SCREEN_HEIGHT * 0.01), 1);
      setScrollPercent(percent);
      Animated.timing(navbarScale, { toValue: 1 + (percent * 0.015), duration: 100, useNativeDriver: true }).start();
    });
    return () => scrollY.removeListener(listener);
  }, [scrollY]);

  // For non-Landing pages, always show full navbar styling
  const effectiveScrollPercent = scrollY ? scrollPercent : 1;
  const navShadow = effectiveScrollPercent > 0.5 ? baseTheme.shadows.navbar : 'none';
  const navBg = effectiveScrollPercent > 0.5 ? theme.surfaceFrosted : 'transparent';

  // Build navigation items based on current route
  const getNavItems = () => {
    if (currentRoute === 'Landing') {
      return [
        { label: 'Features', onPress: () => scrollToSection?.('features'), isActive: false },
        { label: 'How it works', onPress: () => scrollToSection?.('how'), isActive: false },
        { label: 'Pricing', onPress: () => navigation.navigate('Plans'), isActive: false },
      ];
    }
    
    // For all other pages, show consistent navigation
    const items = [
      { label: 'Home', onPress: () => navigation.navigate('Landing'), isActive: currentRoute === 'Landing' },
    ];
    
    if (user) {
      items.push({ label: 'Library', onPress: () => navigation.navigate('Library'), isActive: currentRoute === 'Library' });
    }
    
    items.push({ label: 'Pricing', onPress: () => navigation.navigate('Plans'), isActive: currentRoute === 'Plans' });
    
    return items;
  };

  const navItems = getNavItems();

  return (
    <Animated.View style={[
      styles.navbarContainer, 
      { 
        paddingHorizontal: isMobileNav ? 16 : 48, 
        transform: scrollY ? [{ scale: navbarScale }] : [] 
      }
    ]}>
      <Animated.View style={[styles.navbar, { 
        backgroundColor: navBg,
        paddingVertical: 14 + (effectiveScrollPercent * 4),
        ...Platform.select({ 
          web: { 
            backdropFilter: effectiveScrollPercent > 0.5 ? 'blur(20px)' : 'none', 
            WebkitBackdropFilter: effectiveScrollPercent > 0.5 ? 'blur(20px)' : 'none', 
            boxShadow: navShadow, 
            transition: 'all 0.3s ease-out' 
          } 
        }),
      }]}>
        {/* Logo */}
        <Pressable onPress={() => navigation.navigate('Landing')} style={styles.logoContainer}>
          <View style={[styles.logoIcon, { backgroundColor: theme.primary }]}>
            <View style={[styles.logoNote, { backgroundColor: theme.textOnPrimary }]} />
            <View style={[styles.logoNote2, { backgroundColor: theme.textOnPrimary }]} />
          </View>
          {!isMobileNav && <Text style={[styles.logoText, { color: theme.textPrimary }]}>SheetShed</Text>}
        </Pressable>

        {/* Nav Links */}
        {!isMobileNav && (
          <View style={styles.navLinks}>
            {navItems.map((item, index) => (
              <NavPill 
                key={index}
                label={item.label} 
                onPress={item.onPress} 
                theme={theme}
                isActive={item.isActive}
              />
            ))}
          </View>
        )}

        {/* Right side actions */}
        <View style={styles.navAuth}>
          <Pressable 
            style={[styles.darkModeToggle, { backgroundColor: effectiveScrollPercent > 0.5 ? theme.surfaceHover : 'transparent' }]} 
            onPress={() => toggleDarkMode(!isDark)}
          >
            {isDark ? <SunIcon color={theme.textSecondary} size={20} /> : <MoonIcon color={theme.textSecondary} size={20} />}
          </Pressable>
          
          {user ? (
            // Logged in: show admin badge (if admin) + avatar
            <>
              {user.isAdmin && (
                <Pressable
                  onPress={() => navigation.navigate('Admin')}
                  style={[styles.adminBadge, { backgroundColor: '#EF444420', borderColor: '#EF4444' }]}
                >
                  <Text style={[styles.adminBadgeText, { color: '#EF4444' }]}>Admin</Text>
                </Pressable>
              )}
              <Pressable onPress={() => navigation.navigate('Library')} style={[styles.userAvatar, { backgroundColor: theme.primary }]}>
                <Text style={[styles.userAvatarText, { color: theme.textOnPrimary }]}>
                  {user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                </Text>
              </Pressable>
            </>
          ) : (
            // Not logged in: show Sign In and Get Started
            <>
              {!isMobileNav && (
                <AnimatedButton onPress={() => navigation.navigate('Auth', { mode: 'signin' })} style={styles.signInButton}>
                  <Text style={[styles.signInText, { color: theme.textSecondary }]}>Sign In</Text>
                </AnimatedButton>
              )}
              <AnimatedButton onPress={() => navigation.navigate('Auth', { mode: 'signup' })} style={[styles.signUpButton, { backgroundColor: theme.secondary }]}>
                <Text style={[styles.signUpText, { color: theme.textOnDark }]}>{isMobileNav ? 'Start' : 'Get Started'}</Text>
              </AnimatedButton>
            </>
          )}
        </View>
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  navbarContainer: { 
    position: 'absolute', 
    top: 0, 
    left: 0, 
    right: 0, 
    zIndex: 100, 
    paddingTop: 20 
  },
  navbar: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    borderRadius: baseTheme.borderRadius.pill, 
    paddingHorizontal: 24 
  },
  logoContainer: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    gap: 12 
  },
  logoIcon: { 
    width: 40, 
    height: 40, 
    borderRadius: baseTheme.borderRadius.lg, 
    justifyContent: 'center', 
    alignItems: 'center', 
    position: 'relative' 
  },
  logoNote: { 
    width: 9, 
    height: 9, 
    borderRadius: 5, 
    position: 'absolute', 
    top: 11, 
    left: 11 
  },
  logoNote2: { 
    width: 7, 
    height: 7, 
    borderRadius: 4, 
    position: 'absolute', 
    bottom: 11, 
    right: 11 
  },
  logoText: { 
    fontSize: 20, 
    fontWeight: baseTheme.typography.fontWeight.bold 
  },
  navLinks: { 
    flexDirection: 'row', 
    gap: 6 
  },
  navPill: { 
    paddingHorizontal: 18, 
    paddingVertical: 12, 
    borderRadius: baseTheme.borderRadius.pill, 
    position: 'relative', 
    overflow: 'hidden' 
  },
  navPillBg: { 
    position: 'absolute', 
    top: 0, 
    left: 0, 
    right: 0, 
    bottom: 0, 
    borderRadius: baseTheme.borderRadius.pill 
  },
  navPillText: { 
    fontSize: 15, 
    fontWeight: baseTheme.typography.fontWeight.medium, 
    position: 'relative', 
    zIndex: 1 
  },
  navAuth: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    gap: 10 
  },
  darkModeToggle: { 
    width: 40, 
    height: 40, 
    borderRadius: 20, 
    justifyContent: 'center', 
    alignItems: 'center' 
  },
  signInButton: { 
    paddingHorizontal: 18, 
    paddingVertical: 12 
  },
  signInText: { 
    fontSize: 15, 
    fontWeight: baseTheme.typography.fontWeight.medium 
  },
  signUpButton: { 
    paddingHorizontal: 22, 
    paddingVertical: 12, 
    borderRadius: baseTheme.borderRadius.pill 
  },
  signUpText: { 
    fontSize: 15, 
    fontWeight: baseTheme.typography.fontWeight.semibold 
  },
  userAvatar: { 
    width: 40, 
    height: 40, 
    borderRadius: 20, 
    justifyContent: 'center', 
    alignItems: 'center' 
  },
  userAvatarText: { 
    fontSize: 16, 
    fontWeight: '700' 
  },
  adminBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
  },
  adminBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});

export default Navbar;
